import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import {type ReactNode} from "react";
import * as v from "valibot";
import type {GroupRepo, ItemId, IngredientId, IngredientEntry} from "../../models/GroupRepo";
import {useUnitList} from "../../models/unit-queries";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import {SelectField, TextField} from "../form/fields";
import {useForm} from "../form/Form";
import {quantitySchema} from "../form/form-utils";
import {usePastVariantValues} from "../items/RefineItem";

const Block = ({children, title}: {children: ReactNode; title: ReactNode}) => (
  <Col
    rounded="4"
    borderWidth={1}
    borderColor="default"
    pa="16"
    sp="12"
    width="100%"
    maxWidth="formWidth"
  >
    <Text as="h3" size="16">
      {title}
    </Text>
    {children}
  </Col>
);

const updateVariantFormSchema = v.object({variant: v.string()});

type UpdateVariantProps = {
  ingredient: IngredientEntry;
  groupRepo: GroupRepo;
  pastVariants: string[];
};
const UpdateVariant = (props: UpdateVariantProps) => {
  const {ingredient, groupRepo, pastVariants} = props;

  const handleVariantChange = (nextValue: string) => {
    groupRepo.Ingredients.patch(ingredient.id, {variant: nextValue || null});
  };

  const handleVariantButton = (nextValue: string) => {
    setValue("variant", nextValue);
    handleVariantChange(nextValue);
  };

  const {Form, field, setValue} = useForm({
    initial: {
      variant: ingredient.variant || "",
    },
    submitOnBlur: true,
    schema: updateVariantFormSchema,
    onSubmit: ({variant: nextValue}) => {
      handleVariantChange(nextValue);
    },
  });

  return (
    <Block title={<Trans>Update Variant</Trans>}>
      <Form className={css({sp: "8", display: "flex", flexDir: "column"})}>
        <TextField field={field("variant")} label={<Trans>Variant</Trans>} />
        {pastVariants.length > 0 && (
          <Row wrap sp="4">
            {pastVariants.map((v) => (
              <Button
                key={v}
                active={v === ingredient.variant}
                size="sm"
                onClick={() => handleVariantButton(v === ingredient.variant ? "" : v)}
              >
                {v}
              </Button>
            ))}
          </Row>
        )}
      </Form>
    </Block>
  );
};

const updateQuantityFormSchema = v.object({
  quantity: quantitySchema,
  unit: v.nullable(v.string()),
});

const UpdateQuantity = ({
  ingredient,
  groupRepo,
  onClose,
}: {
  ingredient: IngredientEntry;
  groupRepo: GroupRepo;
  onClose: () => void;
}) => {
  const id = ingredient.id;

  const {Form, field, setValue} = useForm({
    initial: {
      quantity: ingredient.quantity.toString(),
      unit: ingredient.unit,
    },
    submitOnBlur: true,
    schema: updateQuantityFormSchema,
    onSubmit: ({unit, quantity}) => {
      groupRepo.Ingredients.patch(id, {quantity, unit});
    },
  });

  const handleUpdate = (next: number) => {
    setValue("quantity", `${next}`);
    groupRepo.Ingredients.patch(id, {quantity: next});
  };

  const handleDelete = () => {
    groupRepo.Ingredients.delete(id);
    onClose();
  };

  const {i18n} = useLingui();
  const units = useUnitList(groupRepo, i18n);

  return (
    <Row
      rounded="4"
      borderWidth={1}
      borderColor="default"
      pa="16"
      sp="8"
      align="start"
      width="100%"
      maxWidth="formWidth"
    >
      <Text as="h3" size="16" className={css({"@hide": "sm"})} flex="none">
        <Trans>Quantity</Trans>
      </Text>
      <Col>
        <Form className={css({display: "flex", sp: "8"})}>
          <TextField
            field={field("quantity")}
            label={null}
            type="number"
            min="0"
            className={css({width: "100%"})}
          />
          <SelectField field={field("unit")} label={null} options={units} />
        </Form>
      </Col>
      <PlusMinusButtons
        value={ingredient.quantity}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        className={css({ml: "auto"})}
      />
    </Row>
  );
};

type RefineItemProps = {
  id: IngredientId;
  onClose: () => void;
  groupRepo: GroupRepo;
};
const RefineIngredient = (props: RefineItemProps) => {
  const {id, groupRepo, onClose} = props;
  const ingredient = groupRepo.Ingredients.useById(id);
  const item = groupRepo.Items.useById(ingredient?.itemId as ItemId);
  const {pastVariants} = usePastVariantValues(ingredient?.itemId as ItemId, groupRepo);

  if (!item || !ingredient) return <div>No such item</div>;

  return (
    <Col sp="16" align="center">
      <Text
        as="h2"
        size="20"
        textOverflow="ellipsis"
        overflow="hidden"
        textWrap="nowrap"
        maxWidth="100%"
      >
        <Trans>Refine {item.name}</Trans>
      </Text>
      <UpdateQuantity ingredient={ingredient} groupRepo={groupRepo} onClose={onClose} />
      <UpdateVariant ingredient={ingredient} groupRepo={groupRepo} pastVariants={pastVariants} />
    </Col>
  );
};

export default RefineIngredient;
